import React from 'react';
import { Modal, Button, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileExcel, faTimes, faCheck, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { getStatusBadge, getImportSummary } from '../../../utils/importUtils';
import ValidationErrorsTable from './ValidationErrorsTable';
import DatabaseErrorsTable from './DatabaseErrorsTable';
import SkippedUsersTable from './SkippedUsersTable';

/**
 * ImportResultsModal displays the results of a user import operation in a modal dialog.
 * @component
 * @param {boolean} show - Controls the visibility of the modal.
 * @param {function} onHide - Callback to close the modal.
 * @param {Object} importResults - Results data from the import operation.
 * @param {string} importResults.message - Summary message about the import.
 * @param {Object} importResults.results - Detailed results including validation, database, and skipped users.
 * @param {function} onImportAnother - Callback to trigger another import operation.
 * @returns {JSX.Element|null} Modal displaying import results, or null if no results.
 */
const ImportResultsModal = ({ show, onHide, importResults, onImportAnother }) => {
  if (!importResults) return null;

  const summary = getImportSummary(importResults);
  const hasErrors = summary.totalErrors > 0 || summary.skippedCount > 0;

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <div
        style={{
          backgroundColor: '#0a183a',
          color: 'white',
          borderRadius: '15px',
          boxShadow: '0 0 10px rgba(116, 232, 199, 0.651)',
          position: 'relative',
          overflow: 'hidden',
          width: '100%',
          minHeight: '100%'
        }}
      >
        {/* SVG Gradient Background */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none',
            zIndex: 0,
            marginTop: '120px',
            padding: '30px'
          }}
        >
        </div>

        <div style={{ position: 'relative', zIndex: 1, padding: '20px' }}>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h5 className="nucleus_title mb-0 d-flex align-items-center" style={{ color: 'white', fontSize: '20px', whiteSpace: 'nowrap' }}>
              <FontAwesomeIcon icon={faFileExcel} className="mr-2" size="sm" style={{ color: '#74E8C7' }} />
              Import Results
            </h5>
            <Button
              variant="link"
              size="sm"
              onClick={onHide}
              className="p-1 border-0 d-flex align-items-center justify-content-center"
              style={{
                fontSize: '0.8rem',
                color: '#74E8C7',
                minHeight: '24px',
                minWidth: '24px'
              }}
            >
              <FontAwesomeIcon icon={faTimes} size="sm" />
            </Button>
          </div>

          <div>
            <div className="mb-4">
              <h6 style={{ color: '#74E8C7', fontSize: '16px' }}>Summary</h6>
              <div className="mb-2 d-flex flex-wrap gap-2">
                {summary.successCount > 0 && (
                  <span
                    style={{
                      backgroundColor: '#00A28A',
                      color: 'white',
                      padding: '6px 10px',
                      borderRadius: '6px',
                      fontSize: '11px',
                      fontWeight: '600',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      whiteSpace: 'nowrap',
                      minHeight: '24px'
                    }}
                  >
                    <FontAwesomeIcon icon={faCheck} size="sm" />
                    <span>{summary.successCount} success</span>
                  </span>
                )}
                {summary.totalErrors > 0 && (
                  <span
                    style={{
                      backgroundColor: '#EA6759',
                      color: 'white',
                      padding: '6px 10px',
                      borderRadius: '6px',
                      fontSize: '11px',
                      fontWeight: '600',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      whiteSpace: 'nowrap',
                      minHeight: '24px'
                    }}
                  >
                    <FontAwesomeIcon icon={faTimes} size="sm" />
                    <span>{summary.totalErrors} error{summary.totalErrors !== 1 ? 's' : ''}</span>
                  </span>
                )}
                {summary.skippedCount > 0 && (
                  <span
                    style={{
                      backgroundColor: '#F7C46C',
                      color: '#0a183a',
                      padding: '6px 10px',
                      borderRadius: '6px',
                      fontSize: '11px',
                      fontWeight: '600',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      whiteSpace: 'nowrap',
                      minHeight: '24px'
                    }}
                  >
                    <FontAwesomeIcon icon={faExclamationTriangle} size="sm" />
                    <span>{summary.skippedCount} skipped</span>
                  </span>
                )}
              </div>
              <Alert
                variant={summary.successCount > 0 ? 'success' : 'warning'}
                style={{
                  backgroundColor: summary.successCount > 0 ? 'rgba(116, 232, 199, 0.1)' : 'rgba(247, 196, 108, 0.1)',
                  borderColor: summary.successCount > 0 ? '#74E8C7' : '#F7C46C',
                  color: 'white'
                }}
              >
                {importResults.message}
              </Alert>
            </div>

            {hasErrors && (
              <div>
                <h6 style={{ color: '#74E8C7', fontSize: '16px' }}>Detailed Results</h6>

                <ValidationErrorsTable
                  errors={importResults.results?.validation?.errors}
                />

                <DatabaseErrorsTable
                  errors={importResults.results?.database?.errors}
                />

                <SkippedUsersTable
                  skippedUsers={importResults.results?.validation?.skipped}
                />
              </div>
            )}

            <div className="d-flex justify-content-end mt-4">
              <Button
                variant="secondary"
                onClick={onHide}
                className="d-flex align-items-center justify-content-center mr-2 btn2"
                style={{
                  fontSize: '13px',
                  fontWeight: '600',
                  whiteSpace: 'nowrap'
                }}
              >
                Close
              </Button>
              <Button
                variant="primary"
                onClick={onImportAnother}
                className="d-flex align-items-center justify-content-center btn2"
                style={{
                  fontWeight: '600',
                  fontSize: '13px',
                  whiteSpace: 'nowrap'
                }}
              >
                Import Another File
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ImportResultsModal;
